/**
 * The tenant master list: its filter head and its rows.
 *
 * @module components/platform/TenantList
 */

import React from "react";

import { SORT_OPTIONS, type TenantSort, tenantState } from "@/components/platform/tenant-directory";
import {
  countLabel,
  EmptyState,
  Heading,
  MasterItem,
  MasterList,
  SelectField,
  StatusBadge,
  Text,
  TextField,
} from "@/components/ui";
import { ListBones } from "@/components/ui/SkeletonParts";
import type { TenantOnboardingSummary } from "@/platform";

export interface TenantListProps {
  tenants: readonly TenantOnboardingSummary[];
  loading: boolean;
  /** Why the list is empty, when it is. */
  emptyMessage: string;
  selectedTenantId: string;
  onSelect: (tenantId: string) => void;
}

function TenantRow({
  tenant,
  selected,
  onSelect,
}: {
  tenant: TenantOnboardingSummary;
  selected: boolean;
  onSelect: () => void;
}): React.ReactElement {
  const state = tenantState(tenant);
  return (
    <MasterItem
      title={tenant.name}
      meta={tenant.slug ?? undefined}
      // Without this the name concatenates every chip in the row into one
      // run-on string. The chips stay visible; the row announces the tenant.
      aria-label={tenant.name}
      selected={selected}
      onSelect={onSelect}
      data-testid={`platform-console-tenant-${tenant.id}`}
    >
      <span data-testid={`platform-console-state-${tenant.id}`} className="flex flex-wrap gap-1.5">
        <StatusBadge status={state.status} label={state.label} size="sm" />
        {state.alsoLabel ? (
          <StatusBadge status={tenant.onboardingStatus} label={state.alsoLabel} size="sm" />
        ) : null}
      </span>
    </MasterItem>
  );
}

export interface TenantFilterProps {
  /** Every tenant read, before the query narrows it. */
  total: number;
  shown: number;
  loading: boolean;
  query: string;
  onQueryChange: (query: string) => void;
  sort: TenantSort;
  onSortChange: (sort: TenantSort) => void;
}

/** What to look for and how to order it, for the head of the master surface. */
export function TenantFilter({
  total,
  shown,
  loading,
  query,
  onQueryChange,
  sort,
  onSortChange,
}: TenantFilterProps): React.ReactElement {
  // Only once the read has answered: a `0` beside the loading skeleton reads
  // as how many tenants there are.
  const count = loading ? undefined : `${shown} of ${countLabel(total, "tenant")}`;
  return (
    <div className="grid gap-2 p-3" data-testid="platform-console-filters">
      <div className="flex items-center justify-between gap-2">
        <Heading level={2} size="md">
          Tenants
        </Heading>
        {count ? (
          <Text as="span" size="sm" tone="muted" data-testid="platform-console-count">
            {count}
          </Text>
        ) : null}
      </div>
      <TextField
        id="platform-console-filter"
        type="search"
        size="sm"
        value={query}
        onChange={onQueryChange}
        placeholder="Filter tenants"
        aria-label="Filter tenants"
        data-testid="platform-console-filter"
      />
      <SelectField
        id="platform-console-sort"
        size="sm"
        value={sort}
        onChange={(value) => {
          onSortChange(value as TenantSort);
        }}
        options={SORT_OPTIONS.map((option) => ({ ...option }))}
        aria-label="Order tenants by"
        data-testid="platform-console-sort"
      />
    </div>
  );
}

/**
 * @example
 * ```tsx
 * <TenantList tenants={visible} loading={false} … />
 * ```
 */
export function TenantList({
  tenants,
  loading,
  emptyMessage,
  selectedTenantId,
  onSelect,
}: TenantListProps): React.ReactElement {
  return (
    <div data-testid="platform-console-tenants">
      {loading ? (
        // One region, not one per placeholder card: three `Skeleton`s are
        // three `role="status"` elements, all announcing the same wait.
        <div className="p-3" data-testid="platform-console-tenants-loading">
          <ListBones rows={6} label="Loading tenants" />
        </div>
      ) : (
        <MasterList>
          {tenants.map((tenant) => (
            <TenantRow
              key={tenant.id}
              tenant={tenant}
              selected={selectedTenantId === tenant.id}
              onSelect={() => {
                onSelect(tenant.id);
              }}
            />
          ))}
        </MasterList>
      )}
      {!loading && tenants.length === 0 ? (
        <div className="p-3">
          <EmptyState
            icon="buildings"
            title={emptyMessage}
            density="row"
            data-testid="platform-console-tenants-empty"
          />
        </div>
      ) : null}
    </div>
  );
}
